import { db, storage } from './firebase';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { ProjectStandupNote, TimberProject } from '../types';

const COLLECTION = 'projectStandupNotes';
const AUDIO_PREFIX = 'projectStandupAudio';

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ProjectStandupNote {
  const d = snap.data();
  return {
    id: snap.id,
    projectId: d.projectId as string,
    text: (d.text as string) || '',
    audioUrl: (d.audioUrl as string) || undefined,
    audioStoragePath: (d.audioStoragePath as string) || undefined,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
    createdBy: (d.createdBy as string) || '',
    createdByName: (d.createdByName as string) || 'Unknown',
  };
}

export async function uploadStandupAudio(
  projectId: string,
  blob: Blob,
): Promise<{ url: string; storagePath: string }> {
  const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
  const storagePath = `${AUDIO_PREFIX}/${projectId}/${Date.now()}.${ext}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: blob.type || 'audio/webm' });
  const url = await getDownloadURL(storageRef);
  return { url, storagePath };
}

export interface CreateStandupNoteInput {
  text: string;
  audioBlob?: Blob | null;
}

export async function createProjectStandupNote(
  projectId: string,
  input: CreateStandupNoteInput,
  user: { id: string; displayName: string },
): Promise<ProjectStandupNote> {
  const trimmed = input.text.trim();
  if (!trimmed && !input.audioBlob) {
    throw new Error('Add a sentence or record audio before saving.');
  }

  let audioUrl: string | undefined;
  let audioStoragePath: string | undefined;
  if (input.audioBlob && input.audioBlob.size > 0) {
    const uploaded = await uploadStandupAudio(projectId, input.audioBlob);
    audioUrl = uploaded.url;
    audioStoragePath = uploaded.storagePath;
  }

  const now = new Date();
  const payload: Record<string, unknown> = {
    projectId,
    text: trimmed,
    createdAt: Timestamp.fromDate(now),
    createdBy: user.id,
    createdByName: user.displayName,
  };
  if (audioUrl) payload.audioUrl = audioUrl;
  if (audioStoragePath) payload.audioStoragePath = audioStoragePath;

  const docRef = await addDoc(collection(db, COLLECTION), payload);

  return {
    id: docRef.id,
    projectId,
    text: trimmed,
    audioUrl,
    audioStoragePath,
    createdAt: now,
    createdBy: user.id,
    createdByName: user.displayName,
  };
}

/** Denormalized fields to write on timberProjects after a new standup note. */
export function standupDenormFromNote(note: ProjectStandupNote): Partial<TimberProject> {
  return {
    standupNote: note.text || undefined,
    standupNoteAudioUrl: note.audioUrl,
    standupNoteUpdatedAt: note.createdAt,
    standupNoteUpdatedBy: note.createdBy,
    standupNoteUpdatedByName: note.createdByName,
  };
}

/** Writes latest standup snapshot onto timberProjects (clears audio when absent). */
export async function applyStandupDenormToProject(
  projectId: string,
  note: ProjectStandupNote,
): Promise<void> {
  const payload: Record<string, unknown> = {
    standupNote: note.text ? note.text : deleteField(),
    standupNoteUpdatedAt: Timestamp.fromDate(note.createdAt),
    standupNoteUpdatedBy: note.createdBy,
    standupNoteUpdatedByName: note.createdByName,
    standupNoteAudioUrl: note.audioUrl ? note.audioUrl : deleteField(),
    updatedAt: Timestamp.fromDate(new Date()),
  };
  await updateDoc(doc(db, 'timberProjects', projectId), payload as never);
}

export async function createAndApplyProjectStandupNote(
  projectId: string,
  input: CreateStandupNoteInput,
  user: { id: string; displayName: string },
): Promise<ProjectStandupNote> {
  const note = await createProjectStandupNote(projectId, input, user);
  await applyStandupDenormToProject(projectId, note);
  return note;
}

export function subscribeProjectStandupNotes(
  projectId: string,
  callback: (notes: ProjectStandupNote[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
    limit(100),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      console.warn('[subscribeProjectStandupNotes] listener failed:', err);
      onError?.(err);
      callback([]);
    },
  );
}
